// One of Merlin's questions as a card: the question, the quoted words it
// rests on, the decide-by chip, the initiative it belongs to and the Answer /
// Record / Dismiss controls. The Merlin's questions tab lists these, and the
// question pop-up (QuestionModal) shows one on its own (owner ask 2026-10-12:
// a question clicked on the meeting view opens "a shared pop-up with the
// Merlin's questions tab") — the same card in both places, so an answer typed
// in either lands the same way.

import type { ClarifyingQuestion } from '../../services/roadmapReports';
import { questionBody, questionWithoutDecideBy } from './roadmapViewUtils';
import { cardClass } from './questionCardUtils';
import { DecideByChip } from './DecideByChip';
import { EvidenceList, type OpenSourceFn } from './EvidenceList';
import { CitedSources } from './CitedSources';
import { QuestionResponseControls, type RespondFn, type WithdrawFn } from './QuestionResponseControls';
import { RelevanceControls, type MarkRelevanceFn } from './RelevanceControls';
import styles from './RoadmapV2.module.css';

/** Opens a question in the pop-up; `siblings` is the list it was opened from, for prev / next. */
export type OpenQuestionFn = (q: ClarifyingQuestion, siblings: ClarifyingQuestion[]) => void;

export interface QuestionCardProps {
  q: ClarifyingQuestion;
  /** Today as ISO, for the decide-by chip. */
  today: string;
  reportDate: string | null;
  /** The initiative's cited sources, listed when the question has no quote yet. */
  sources?: readonly string[];
  /** A red lead-in above the question (the "Needs an answer first" block). */
  lead?: string;
  /** The card sits in its addressee's own list: the "Name:" lead-in is dropped. */
  own?: boolean;
  /** Opens the initiative pop-up. */
  onSelect: (key: string) => void;
  onOpenSource?: OpenSourceFn;
  /** Both given → Answer / Record / Dismiss; otherwise the card is read-only. */
  onRespond?: RespondFn;
  onWithdraw?: WithdrawFn;
  /** Given → the question text is a button that opens the pop-up. */
  onOpen?: OpenQuestionFn;
  /** Given → the Relevant? Yes / No buttons (kanban 3709db93), for any reader. */
  onMark?: MarkRelevanceFn;
  /** The list this card sits in, handed to onOpen for prev / next. */
  siblings?: ClarifyingQuestion[];
  /** List item on the tab, a plain block in the pop-up. */
  as?: 'li' | 'div';
  /** The pop-up names the initiative in its header, so the card's pill is off there. */
  showInitiative?: boolean;
}

export function QuestionCard({
  q,
  today,
  reportDate,
  sources = [],
  lead,
  own = false,
  onSelect,
  onOpenSource,
  onRespond,
  onWithdraw,
  onOpen,
  onMark,
  siblings = [],
  as: Tag = 'li',
  showInitiative = true,
}: QuestionCardProps) {
  const quotes = q.evidence ?? [];
  const text = own ? questionBody(q) : questionWithoutDecideBy(q.question);
  return (
    <Tag className={cardClass(q.response)} data-testid="question-card">
      {lead && <div className={styles.askLead}>{lead}</div>}
      {onOpen ? (
        <button
          type="button"
          className={`${styles.askQuestion} ${styles.askQuestionBtn}`}
          onClick={() => onOpen(q, siblings)}
          title={own ? q.question : undefined}
          data-action="ask_open_question"
          data-target-initiative-key={q.initiative_key}
        >
          {text}
        </button>
      ) : (
        <div className={styles.askQuestion} title={own ? q.question : undefined}>{text}</div>
      )}
      {quotes.length > 0 ? (
        <EvidenceList evidence={quotes} onOpenSource={onOpenSource} aria-label="What this question rests on" />
      ) : (
        <CitedSources
          sources={sources}
          onOpenSource={onOpenSource}
          lead="No quote recorded yet; Merlin adds the words behind each question on its next run."
        />
      )}
      <div className={styles.askCardFoot} data-testid="ask-foot">
        <DecideByChip question={q.question} today={today} reportDate={reportDate} />
        {showInitiative && (
          <button
            type="button"
            className={styles.askInitiative}
            onClick={() => onSelect(q.initiative_key)}
            title="Open the initiative this question belongs to"
            data-action="ask_open_initiative"
            data-target-initiative-key={q.initiative_key}
          >
            {q.title}
          </button>
        )}
      </div>
      {onMark && (
        <RelevanceControls
          identity={{ initiative_key: q.initiative_key, question: q.question }}
          relevance={q.relevance}
          onMark={onMark}
        />
      )}
      {onRespond && onWithdraw && (
        <QuestionResponseControls
          identity={{ initiative_key: q.initiative_key, question: q.question }}
          response={q.response}
          onRespond={onRespond}
          onWithdraw={onWithdraw}
        />
      )}
    </Tag>
  );
}
